<template>
  <app-card
    width="100%"
    :height="height"
    ref="scrollWrap"
    :color="backgroundColor"
    class="d-flex flex-column fill-height"
  >
    <AppListContainerHeader
      total-text="Results"
      :total="total"
      :selectable="selectable"
      :selected="selected.length"
      :refreshable="!hideRefresh"
      :no-gutters="noGutters"
      :plot-test="$vuetify.breakpoint.smAndDown"
      :force-fullscreen="forceFullscreen"
      @click:refresh="$emit('click:refresh')"
    >
      <sorting-bar
        v-if="!hideFilters"
        dropdown
        :dropdown-width="$vuetify.breakpoint.mdAndDown || forceFullscreen ? '100%' : 200"
        prependTitleClass="subtitle-1 subsecondary--text"
        v-model="activeSortV"
        :items="sortingItems"
        class="ml-0 sorting-bar"
      />

      <template #actions-prepend>
        <app-card
          v-if="!hideFilters"
          color="transparent"
          :width="fullscreen ? '100%' : 605"
          class="ma-0 d-flex gap-2 flex-wrap"
          :class="fullscreen ? 'flex-column' : 'flex-row'"
        >
          <fetch-input
            class="flex flex-equal flex-grow"
            :value="author"
            :provider="getContributorsData"
            label="Author"
            placeholder="Author"
            prepend-inner-icon="$vuetify.icons.author"
            hide-details
            clearable
            @input="$emit('update:author', $event)"
          />
          <fetch-input
            class="flex flex-equal flex-grow"
            :value="license"
            :provider="getLicenses"
            label="License"
            placeholder="License"
            prepend-inner-icon="$vuetify.icons.license"
            hide-details
            clearable
            @input="$emit('update:license', $event)"
          />
        </app-card>
      </template>

      <template #actions-left>
        <slot name="actions-left"/>
      </template>

      <template #actions>
        <slot name="actions"/>
      </template>

      <template #actions-right>
        <div v-show="!hideZoom">
          <material-view-settings-menu
            ref="zoomMenu"
            :key="settingKey"
            :item-height-offset="itemHeightOffset"
            :step="$vuetify.breakpoint.smAndDown ? 20 : 50"
            :show-unpublished.sync="showUnpublishedVal"
            :show-unpublished-hidden="showUnpublishedHidden"
            @update:item-height-offset="$emit('update:item-height-offset', $event)"
          >
            <slot name="settings"></slot>
          </material-view-settings-menu>
        </div>
      </template>
    </AppListContainerHeader>

    <div class="ma-0 pa-2 overflow-hidden flex-grow-1 d-flex flex-column">
      <RecycleScroller
        class="overflow-x-hidden fill-height"
        :items="itemsStartIndexesObjects"
        :item-size="itemHeight"
        key-field="id"
        :prerender="4"
      >
        <template #default="{ item }">
          <material-list-view-row
            :all-items="itemsToShow"
            :height="itemHeight"
            :start="item.id"
            :amount="itemsPerRow"
            :card-bottom-size="cardBottomSize"
            :background-color="backgroundColor"
            :selected="selectedV"
            :selectable="selectable"
            @show:material="onShowMaterial"
            @click:image="$emit('click:open', $event)"
            @click:open="$emit('click:open', $event)"
            @click:download="onClickDownload($event)"
            @click:favorite="onClickFavorite($event)"
            @click:item="toggleSelected"
            @click:new-item="$emit('click:new-item')"
          >
            <template #item-hover="{ hover, item }"><slot name="item-hover" :hover="hover" :item="item"></slot></template>
          </material-list-view-row>
        </template>
      </RecycleScroller>

      <v-progress-linear
        v-show="loading"
        indeterminate
        absolute
        bottom
        rounded
        color="grey"
      />
    </div>
  </app-card>
</template>

<script>
  import MaterialListViewRow from "./MaterialListViewRow";
  import MaterialViewSettingsMenu from "./MaterialViewSettingsMenu";
  import SortingBar from "@/components/SortingBar";
  import AppListContainerHeader from '@/components/AppListContainerHeader';
  import arraysUtils from "@/utils/arrays";
  import FetchInput from '@/components/FetchInput';
  import config from '@/config';

  export default {
    name: "MaterialListView",

    components: {
      MaterialListViewRow,
      MaterialViewSettingsMenu,
      SortingBar,
      FetchInput,
      AppListContainerHeader,
    },

    props: {
      author: {
        type: String,
      },
      license: {
        type: String
      },
      backgroundColor: {
        type: String,
      },
      height: {
        type: [Number, String]
      },
      itemHeight: {
        type: Number,
        default: 300,
      },
      itemHeightOffset: {
        type: Number,
        default: 300,
      },
      itemsPerRow: {
        type: Number,
        default: 4,
      },
      items: Array,
      loading: {
        type: Boolean,
        default: false,
      },
      total: {
        type: Number,
        default: 0,
      },
      cardBottomSize: {
        type: Number,
        default: 70,
      },
      activeSort: {
        type: Array,
        default: () => [{ id: 'published_date', ordering: '-' }],
      },
      selectable: {
        type: Boolean,
        default: false,
      },
      selected: {
        type: Array,
        default: () => [],
      },
      showUnpublishedValue: {
        type: Boolean,
        default: undefined,
      },
      showUnpublishedHidden: {
        type: Boolean,
        default: false,
      },
      showCreateButton: {
        type: Boolean,
        default: false,
      },
      noGutters: {
        type: Boolean,
        default: false,
      },
      hideRefresh: {
        type: Boolean,
        default: false,
      },
      hideZoom: {
        type: Boolean,
        default: false,
      },
      hideFilters: {
        type: Boolean,
        default: false,
      },
      connectToGlobalSettings: {
        type: Boolean,
        default: false,
      },
      lastItemsTriggerOffset: {
        type: Number,
        default: (config.restrictions.MATERIALS_PRELOADING_CHUNKS * config.restrictions.MATERIALS_LOADING_LIMIT),
      },
    },

    data() {
      return {
        showUnpublishedVal: false,
        sortingItems: [{
          id: 'title',
          title: 'Title'
        }, {
          id: 'published_date',
          title: 'Publish date'
        }],
        activeSortV: [],
        showCreateButtonV: false,
        selectedV: [],
        settingKey: 1,
        forceFullscreen: false
      };
    },

    computed: {

      showUnpublished() {
        if (this.showUnpublishedValue !== undefined) return this.showUnpublishedValue;
        return this.$can('update', this.$enums.Entity.MATERIAL) && this.showUnpublishedVal;
      },

      itemsToShow() {
        let { showUnpublished, $enums, items } = this;
        items = items ?? [];
        if (showUnpublished) {
          return items;
        }
        return items.filter( item => item.status === $enums.Status.PUBLISHED.key );
      },

      itemsStartIndexes() {
        // if need to showCreateButtonV that list starts with -1 else with 0
        let { itemsToShow, itemsPerRow, showCreateButtonV } = this;
        let totalLength = itemsToShow.length ?? 0;
        if (showCreateButtonV) totalLength += 1;

        let rowsAmount = Math.ceil(totalLength / itemsPerRow);
        let indexesArray = new Array(rowsAmount);

        let start = showCreateButtonV ? -1 : 0;
        for (let i = 0; i < indexesArray.length; i += 1) {
          indexesArray[i] = start;
          start += itemsPerRow;
        }

        return indexesArray;
      },

      itemsStartIndexesObjects() {
        return this.itemsStartIndexes.map(it => ({ id: it }));
      },

      fullscreen() {
        return this.$vuetify.breakpoint.mdAndDown || this.forceFullscreen
      }
    },

    watch: {
      activeSort: {
        immediate: true,
        deep: true,
        handler(v) {
          if (arraysUtils.equal(this.activeSortV, v)) return;
          this.activeSortV = [ ...v ];
        },
      },

      activeSortV: {
        immediate: true,
        deep: true,
        handler(v) {
          this.$emit('update:active-sort', v);
          this.$bus.$emit('change:materials-active-sort', v);
        },
      },

      selected: {
        immediate: true,
        deep: true,
        handler(v) {
          if (!this.selectable) return;
          if (arraysUtils.equal(this.selectedV, v)) return;
          this.selectedV = v;
        },
      },
    },

    created() {
      if (this.connectToGlobalSettings) {
        this.$bus.$on('zoom:decrease', this.onZoomDecrease);
        this.$bus.$on('zoom:increase', this.onZoomIncrease);
      }
    },

    mounted() {
      this.$bus.$on('open:collection-form-materials', this.setFullscreen);
      setTimeout( () => {
        // this just fix error with recycle scroller created() handle with undefined item
        this.showCreateButtonV = this.showCreateButton;
        // this just fix error with hidden settings menu button
        this.settingKey = 2;
      }, 0)
    },

    beforeDestroy() {
      this.$bus.$off('open:collection-form-materials', this.setFullscreen);
      if (this.connectToGlobalSettings) {
        this.$bus.$off('zoom:decrease', this.onZoomDecrease);
        this.$bus.$off('zoom:increase', this.onZoomIncrease);
      }
    },

    methods: {

      setFullscreen() {
        this.forceFullscreen = true;
      },

      trackDownload(item, { label }) {
        const [size, bitdepth] = [...label.split(' ', 2), '', ''].slice(0, 2);
        const { id, title } = item;
        this.$gtag.event('download_material', { id, title, label, size, bitdepth });
      },

      onZoomDecrease() {
        this.$refs.zoomMenu?.decreaseOffset();
      },

      onZoomIncrease() {
        this.$refs.zoomMenu?.increaseOffset();
      },

      onClickDownload(item) {
        this.$set(item, 'downloadLoading', true);
        const packageId = item.packages[0];
        this.$api.packages.download(packageId)
        .finally( () => {
          this.$set(item, 'downloadLoading', false);
        });
        this.$api.packages.get(packageId)
        .then( response => {
          this.trackDownload(item, response.data);
        })
      },

      onClickFavorite(item) {
        this.$set(item, 'loadingFavorite', true);
        let promise;
        if (!item.favorite) {
          promise = this.$api.materials.addToFavorites(item);
        } else {
          promise = this.$api.materials.removeFromFavorites(item);
        }
        promise.finally( () => {
          this.$set(item, 'loadingFavorite', false);
          this.$bus.$emit('change:material', { old: { ...item }, saved: { ...item, favorite: !item.favorite } })
        });
      },

      onShowMaterial(item) {
        this.$emit('show:material', item);
        const { lastItemsTriggerOffset, loading, itemsToShow } = this;
        if (itemsToShow.length < lastItemsTriggerOffset) {
          this.$emit("scroll:last");
          return;
        }
        if (loading) return;
        let triggerItem = itemsToShow[itemsToShow.length - lastItemsTriggerOffset];
        let triggerItem1 = itemsToShow[itemsToShow.length - Math.ceil(lastItemsTriggerOffset / 2)];
        let triggerItem2 = itemsToShow[itemsToShow.length - Math.ceil(lastItemsTriggerOffset / 4)];
        let lastItem = itemsToShow[itemsToShow.length - 1];
        let isItemLast = item.id === lastItem.id ||
            triggerItem?.id === item.id ||
            triggerItem1?.id === item.id ||
            triggerItem2?.id === item.id;
        if (!isItemLast) return;
        this.$emit("scroll:last");
      },

      toggleSelected(item) {
        if (!this.selectable) return;

        const { selectedV } = this;
        let index = selectedV.indexOf(item.id);

        if (index === -1) {
          selectedV.push(item.id);
        } else {
          selectedV.splice(index, 1);
        }
        this.$emit('update:selected', selectedV);
      },

      getContributorsData() {
        return this.$api.materials.getContributorsData();
      },

      getLicenses() {
        return this.$api.licenses.getLicensesData(true);
      }
    },
  }
</script>